(function () {
    "use strict";

    function getElementHeight(selector) {
        const element = document.querySelector(selector);
        return element ? element.offsetHeight : 0;
    }

    function getWindowData() {
        return {
            windowHeight: Math.min(window.screen.height, window.innerHeight),
            footerHeight: getElementHeight('[data-section-type*="footer"]'),
            headerHeight: getElementHeight("[data-header-height]"),
            stickyHeaderHeight: document.querySelector("[data-header-sticky]") ? getElementHeight("[data-header-height]") : 0,
            collectionNavHeight: getElementHeight("[data-collection-nav]"),
            logoHeight: (() => {
                const logoHeight = getElementHeight("[data-footer-logo]");
                return logoHeight > 0 ? logoHeight + 20 : 0;
            })()
        };
    }

    function isDesktopView() {
        const smallSize = window.theme?.sizes?.small ?? 768; // Default to 768px if undefined
        return (document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth) >= smallSize;
    }

    customElements.get("timeline-component") || 
    customElements.define("timeline-component", class extends HTMLElement {
        constructor() {
            super();
            this.rows = this.querySelectorAll("[data-timeline-row]");
            if (this.rows.length < 2) return;

            this.rowsWidth = 0;
            this.rowsHeight = [];
            this.holderTop = this.getBoundingClientRect().top + window.scrollY;
            this.holderHeight = this.offsetHeight;
            this.buttons = this.querySelectorAll("[data-timeline-button]");
            this.rowsHolder = this.querySelector("[data-timeline-rows]");
            this.requestAnimation = null;
            this.isDesktopView = isDesktopView();
            this.isScrollEnabled = false;

            this.scrollEvent = (event) => this.scrollEvents(event);
            this.resizeEvent = () => this.resizeEvents();
        }

        connectedCallback() {
            this.calculateRowsDimensions();
            this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

            if (this.isDesktopView && !this.isScrollEnabled) {
                this.isScrollEnabled = true;
                document.addEventListener("theme:scroll", this.scrollEvent);
            }

            this.rowsHolder.addEventListener("scroll", this.scrollEvent);
            document.addEventListener("theme:resize:width", this.resizeEvent);

            if (this.buttons.length) {
                this.buttons.forEach((button, index) => {
                    button.addEventListener("click", (event) => {
                        event.preventDefault();
                        const clickedRow = event.currentTarget.closest("[data-timeline-row]");
                        if (clickedRow) {
                            if (isDesktopView()) {
                                const maxRowHeight = Math.max(...this.rowsHeight);
                                const singleRowHeight = this.holderHeight / this.rows.length;
                                const offsetTop = this.getBoundingClientRect().top;

                                const { stickyHeaderHeight } = getWindowData();
                                window.scrollTo({
                                    top: (index > 0 && maxRowHeight < singleRowHeight ? singleRowHeight * index + offsetTop : offsetTop) + 1 - stickyHeaderHeight,
                                    left: 0,
                                    behavior: "smooth"
                                });
                            } else {
                                const padding = 16;
                                const scrollLeft = clickedRow.offsetLeft - padding;
                                this.rowsHolder.scrollTo({
                                    left: scrollLeft,
                                    behavior: "smooth"
                                });
                            }
                        }
                    });
                });
            }
        }

        resizeEvents() {
            this.holderTop = this.getBoundingClientRect().top + window.scrollY;
            this.holderHeight = this.offsetHeight;
            this.calculateRowsDimensions();
            this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

            const desktopView = isDesktopView();
            if (desktopView && !this.isScrollEnabled) {
                this.isScrollEnabled = true;
                document.addEventListener("theme:scroll", this.scrollEvent);
            } else if (!desktopView && this.isScrollEnabled) {
                this.isScrollEnabled = false;
                document.removeEventListener("theme:scroll", this.scrollEvent);
            }
        }

        scrollEvents(event) {
            if (!this.requestAnimation) {
                this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(event.currentTarget));
            }
        }

        removeAnimationFrame() {
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation);
                this.requestAnimation = null;
            }
        }

        calculateRowsDimensions() {
            if (this.rows.length) {
                this.rowsHeight = [];
                this.rowsWidth = 0;
                let accumulatedHeight = 0;

                this.rows.forEach((row, index) => {
                    this.rowsHeight.push(row.offsetHeight);
                    this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

                    if (window.Shopify?.designMode) {
                        const editorRows = this.querySelectorAll("[data-timeline-row-editor]");
                        if (editorRows.length) {
                            accumulatedHeight += row.offsetHeight;
                            editorRows[index]?.style.setProperty("--row-height-min", `${row.offsetHeight}px`);
                            editorRows[index + 1]?.style.setProperty("--row-top-mobile", `${accumulatedHeight}px`);
                        }
                    }
                });
            }
        }

        calculatePosition(scrollTarget = null) {
            this.removeAnimationFrame();

            const desktopView = isDesktopView();
            const rowHeight = this.holderHeight / this.rows.length;
            let holderTop = this.holderTop;
            const viewportHeight = window.innerHeight;
            const scrollThreshold = window.scrollY + 1.5 * viewportHeight;
            let scrollPercentage = 0;

            if (!desktopView && scrollTarget) {
                const scrollLeft = scrollTarget.scrollLeft - 16;
                const holderWidth = scrollTarget.offsetWidth;
                scrollPercentage = (scrollLeft / (this.rowsWidth - holderWidth)) * 100;
                this.style.setProperty("--percent-mobile", `${scrollPercentage}%`);
            }

            if (desktopView && (scrollThreshold < this.holderTop || scrollThreshold > this.holderTop + this.holderHeight + viewportHeight)) {
                return;
            }

            this.rows.forEach((row, index) => {
                if (index > 0) {
                    const positionPercentage = (row.offsetLeft / this.rowsWidth) * 100;
                    let isActive = scrollPercentage > positionPercentage;

                    if (desktopView) {
                        isActive = holderTop + rowHeight < scrollThreshold;
                        holderTop - rowHeight < scrollThreshold && row.previousElementSibling?.style.setProperty("--percent-desktop", `${(scrollThreshold - holderTop) / rowHeight * 100}%`);
                    }

                    row.classList.toggle("is-selected", isActive);
                }
                holderTop += rowHeight;
            });
        }

        disconnectedCallback() {
            if (this.isScrollEnabled) {
                this.isScrollEnabled = false;
                document.removeEventListener("theme:scroll", this.scrollEvent);
            }
            this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
            document.removeEventListener("theme:resize:width", this.resizeEvent);
        }

        onBlockSelect(event) {
            const blockScroll = this.querySelector("[data-block-scroll]");
            if (blockScroll) {
                const selectedElement = event.srcElement;
                const index = [...selectedElement.parentElement.children].indexOf(selectedElement);
                const targetRow = this.rows[index];
                if (targetRow) {
                    blockScroll.scrollTo({
                        top: 0,
                        left: targetRow.offsetLeft,
                        behavior: "smooth"
                    });
                }
            }
        }
    });
})();
